/* eslint-disable @typescript-eslint/no-explicit-any */
import "react-toastify/dist/ReactToastify.css";
import { buildUrl } from "../../utils/bindUrl";
import {
  IActivity,
  IResponseListActivity,
  IResponseShowActivity,
} from "../../interfaces/activities";
import { IEntry, IResponseShowEntry } from "../../interfaces/entries";
import qs from "qs";
import { api } from "../api/httpClient";
import { IListParams } from "@/interfaces/serviceInterface";

const BASE = "/activities";

// serializer padrão (arrays em brackets, ignora null/undefined)
const serialize = (p: any) =>
  qs.stringify(p, { arrayFormat: "brackets", skipNulls: true, encode: true });

export async function listActivities({
  filter,
  includes,
  page,
  limit,
  select,
  sort, // ✅ nossa alteração
}: IListParams) {
  const { data } = await api.get<IResponseListActivity>(BASE, {
    params: {
      filter,
      sort, // ✅ nossa alteração
      includes,
      page,
      limit,
      select,
    },
    paramsSerializer: (params) => serialize(params),
  });

  return data;
}

export async function showActivity({
  id,
  includes,
}: {
  id: string | number;
  includes?: string[];
}) {
  const url = buildUrl(`${BASE}/{id}`, { id });
  const { data } = await api.get<IResponseShowActivity>(url, {
    params: { includes },
    paramsSerializer: (params) => serialize(params),
  });
  return data;
}

/** Remove totalCost do payload — é calculado pelo backend e não deve ser enviado. */
function stripReadOnlyFields(activity: IActivity): Omit<IActivity, "totalCost"> {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { totalCost: _omit, ...rest } = activity as IActivity & { totalCost?: unknown };
  return rest;
}

export async function createActivity({ activity }: { activity: IActivity }) {
  const { data } = await api.post<IResponseShowActivity>(BASE, {
    activity: stripReadOnlyFields(activity),
  });
  return data;
}

export async function updateActivity({
  id,
  activity,
}: {
  id: string | number;
  activity: IActivity;
}) {
  const url = buildUrl(`${BASE}/{id}`, { id });
  const { data } = await api.put<IResponseShowActivity>(url, {
    activity: stripReadOnlyFields(activity),
  });
  return data;
}

export async function deleteActivities({ ids }: { ids: number[] }) {
  // envia como query string: /activities?ids[]=1&ids[]=2...
  // validateStatus => true evita cair no interceptor de erro (toast automático)
  const resp = await api.delete<any>(BASE, {
    params: { ids },
    paramsSerializer: (params) => serialize(params),
    validateStatus: () => true,
  });
  return { status: resp.status, data: resp.data };
}

export async function createActivityEntry({
  idActivity,
  activityEntry,
}: {
  activityEntry: IEntry;
  idActivity: number;
}) {
  const url = buildUrl(`${BASE}/{idActivity}/entries`, { idActivity });
  const { data } = await api.post<IResponseShowEntry>(url, { activityEntry });
  return data;
}

// ---------------- DASHBOARD (mantido do sistema atual) ----------------


export async function getPerformedActivitiesTotal(
  year?: number,
  month?: number,
  serviceName?: string,
  scopeName?: string | string[]
) {
  const url = `${BASE}/dashboard/performed-total`;
  const { data } = await api.get<{ total: number }>(url, {
    params: { year, month, serviceName, scopeName },
    paramsSerializer: (params) => qs.stringify(params, {
      arrayFormat: 'repeat',
      skipNulls: true,
      encode: true
    }),
  });
  return data;
}

export async function getProblemsTotal(
  year?: number,
  month?: number,
  serviceName?: string,
  scopeName?: string | string[]
) {
  const url = `${BASE}/dashboard/problems-total`;
  const { data } = await api.get<{ total: number }>(url, {
    params: { year, month, serviceName, scopeName },
    paramsSerializer: (params) => qs.stringify(params, {
      arrayFormat: 'repeat',
      skipNulls: true,
      encode: true
    }),
  });
  return data;
}

export async function getTopOutsourced(
  year?: number,
  month?: number,
  serviceName?: string,
  scopeName?: string | string[]
) {
  const url = `${BASE}/dashboard/top-outsourced`;
  const { data } = await api.get<Array<{ name: string; count: number }>>(url, {
    params: { year, month, serviceName, scopeName },
    paramsSerializer: (params) => qs.stringify(params, {
      arrayFormat: 'repeat',
      skipNulls: true,
      encode: true
    }),
  });
  return data;
}

export async function getTopPeriods(
  year?: number,
  month?: number,
  serviceName?: string,
  scopeName?: string | string[]
) {
  const url = `${BASE}/dashboard/top-periods`;
  const { data } = await api.get<{
    topMonth: { month: string; year: number; count: number };
    topYear: { year: number; count: number };
  }>(url, {
    params: { year, month, serviceName, scopeName },
    paramsSerializer: (params) => qs.stringify(params, {
      arrayFormat: 'repeat',
      skipNulls: true,
      encode: true
    }),
  });
  return data;
}

export async function getTopRegions(
  year?: number,
  month?: number,
  serviceName?: string,
  scopeName?: string | string[]
) {
  const url = `${BASE}/dashboard/top-regions`;
  const { data } = await api.get<
    Array<{ name: string; count: number; percentage: number }>
  >(url, {
    params: { year, month, serviceName, scopeName },
    paramsSerializer: (params) => qs.stringify(params, {
      arrayFormat: 'repeat',
      skipNulls: true,
      encode: true
    }),
  });
  return data;
}

export async function getPerformedHistory(
  year?: number,
  month?: number,
  serviceName?: string,
  scopeName?: string | string[]
) {
  const url = `${BASE}/dashboard/performed-history`;
  const { data } = await api.get<Array<{ label: string; value: number }>>(url, {
    params: { year, month, serviceName, scopeName },
    paramsSerializer: (params) => qs.stringify(params, {
      arrayFormat: 'repeat',
      skipNulls: true,
      encode: true
    }),
  });
  return data;
}

export async function getTopTeams(year?: number) {
  const url = `${BASE}/dashboard/top-teams`;
  const { data } = await api.get<Array<{ name: string; outsourced: string; count: number }>>(url, {
    params: { year }
  });
  return data;
}

export async function getActiveTeamsCount(year?: number) {
  const url = `${BASE}/dashboard/active-teams-count`;
  const { data } = await api.get<{ total: number }>(url, {
    params: { year }
  });
  return data;
}
